export type TutorialStepId =
  | 'move'
  | 'jet-ski'
  | 'fire-weapon'
  | 'throw-grenade'
  | 'activate-pack'
  | 'deploy-mine'
  | 'enter-vehicle'
  | 'objective';

export type TutorialStep = {
  id: TutorialStepId;
  title: string;
  description: string;
  keyHint: string;
};

export type TutorialProgress = {
  completedStepIds: TutorialStepId[];
  dismissed: boolean;
};

const STORAGE_KEY = 'tribes2.tutorialProgress.v1';

export const TUTORIAL_STEPS: TutorialStep[] = [
  { id: 'move', title: 'Move', description: 'Use movement keys to get off the stand and build control.', keyHint: 'WASD' },
  { id: 'jet-ski', title: 'Jet / Ski', description: 'Hold jump to jet over ridges, then carry speed along slopes.', keyHint: 'SPACE' },
  { id: 'fire-weapon', title: 'Fire Weapon', description: 'Land a shot or pressure a bot with your primary weapon.', keyHint: 'LMB' },
  { id: 'throw-grenade', title: 'Throw Grenade', description: 'Use a grenade to clear a route or disrupt a chase.', keyHint: 'F' },
  { id: 'activate-pack', title: 'Activate Pack', description: 'Trigger your selected pack before a commit or escape.', keyHint: 'R' },
  { id: 'deploy-mine', title: 'Deploy Mine', description: 'Place a mine or deployable to deny a lane.', keyHint: 'R / PACK' },
  { id: 'enter-vehicle', title: 'Enter Vehicle', description: 'Board a friendly grav vehicle from a powered pad.', keyHint: 'V' },
  { id: 'objective', title: 'Attack / Defend Objective', description: 'Pressure the enemy flag or stabilize your base defense.', keyHint: 'Z / X' },
];

const STEP_IDS = new Set<TutorialStepId>(TUTORIAL_STEPS.map((step) => step.id));

function normalizeProgress(value: unknown): TutorialProgress {
  const raw = value && typeof value === 'object' ? value as Partial<TutorialProgress> : {};
  const completedStepIds = Array.isArray(raw.completedStepIds)
    ? raw.completedStepIds.filter((id): id is TutorialStepId => STEP_IDS.has(id as TutorialStepId))
    : [];

  return {
    completedStepIds: Array.from(new Set(completedStepIds)),
    dismissed: raw.dismissed === true,
  };
}

export function readTutorialProgress(): TutorialProgress {
  if (typeof window === 'undefined') {
    return { completedStepIds: [], dismissed: false };
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return normalizeProgress(stored ? JSON.parse(stored) : null);
  } catch {
    return { completedStepIds: [], dismissed: false };
  }
}

function writeTutorialProgress(progress: TutorialProgress) {
  const normalized = normalizeProgress(progress);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch {
      // Tutorial persistence is optional; gameplay should never depend on storage.
    }
  }
  return normalized;
}

export function completeTutorialStep(stepId: TutorialStepId): TutorialProgress {
  const current = readTutorialProgress();
  if (current.completedStepIds.includes(stepId)) {
    return current;
  }

  return writeTutorialProgress({
    ...current,
    completedStepIds: [...current.completedStepIds, stepId],
  });
}

export function dismissTutorial(): TutorialProgress {
  return writeTutorialProgress({ ...readTutorialProgress(), dismissed: true });
}

export function resetTutorialProgress(): TutorialProgress {
  return writeTutorialProgress({ completedStepIds: [], dismissed: false });
}

export function getCurrentTutorialStep(progress: TutorialProgress): TutorialStep | null {
  if (progress.dismissed) {
    return null;
  }

  return TUTORIAL_STEPS.find((step) => !progress.completedStepIds.includes(step.id)) ?? null;
}
